<template>
  <div id="ticketDetails">

    <div class="section-content" ref="scrollArea">
      <template v-if="selected">

        <div class="vo-card">
          <div class="header">
            <div class="status" :style="{backgroundColor: $store.getters.company.statuses[selected.status].color}"></div>
            <div class="left">
              <div class="unit">{{ unit | showProp('title') }}</div>
              <div class="property">{{ property | showProp('name') }}</div>
            </div>
            <div class="right">
              <div class="date">{{ selected.created | dateShorthand }}</div>
              <div class="status-text">{{ selected.status }}</div>
            </div>
          </div>
          
          <div class="details">
          
            <div class="section">
              <div class="section-title">Ticket Details <span class="number">#{{ selected.number }}</span></div>
              <div class="title-value">{{ selected.title }}</div>
              <div v-if="editing" class="description">
                <v-text-field ref="inputDescription" :disabled="descLockout" :rules="rules.description" no-resize multi-line :rows="3" label="Description" v-model="ticket.body"></v-text-field>
              </div>
              <div v-else class="description">{{ ticket.body }}</div>
            </div>

            <div class="section">
              <div class="section-title">
                Images
                <div class="button">
                  <v-btn flat fab small :disabled="selected.images.length >= 15" @click="routeAddQuery('dialog', 'ticket-add-images')">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </div>
              </div>

              <div class="image-preview" v-if="(selected.images && selected.images.length > 0) || (selected.pendingImages && selected.pendingImages > 0)">
                
                <!-- LOADED IMAGES -->
                <div class="image-thumb" v-for="(image, i) in selected.images" :key="'image' + i" :style="{'background-image': 'url(' + image.uri + ')'}" @click="openGallery(i)"></div>

                <!-- PENDING IMAGES -->
                <template v-if="selected.pendingImages && selected.pendingImages > (selected.images ? selected.images.length : 0)">
                  <div class="image-thumb" v-for="(image, i) in selected.pendingImages - (selected.images ? selected.images.length : 0)" :key="'pending' + i">
                    <v-progress-circular style="position: absolute; margin: 0 auto; top: 37px; left: 37px;" indeterminate color="primary" :size="40"></v-progress-circular>
                  </div>
                </template>

                <div class="clear"></div>
              </div>
              <div v-else>
                <div class="images-empty">No Images</div>
              </div>
            </div>

            <div class="section" style="margin-bottom: 0px">
              <div class="section-title">Unit Details</div>

              <div class="icon-section" v-if="unit || property">
                <i class="icon mdi mdi-24px mdi-home"></i>
                
                <div class="item" :class="{selectable: hasPermissionsInProperty(['unit'], selected.property)}" @click="openUnit" v-if="unit">{{ unit | showProp('title') }}</div>
                <div class="item" :class="{selectable: hasPermissions(['property'])}" v-if="property" @click="openProperty">{{ property | showProp('name') }}</div>
              </div>
              
              <div class="icon-section" v-if="property && property.address">
                <i class="icon mdi mdi-24px mdi-map-marker"></i>

                <div class="item">
                  <span v-if="property.address.line2.length">{{ property.address.line2 }}</span>
                  {{ property.address.line1 }}
                </div>
                <div class="item">{{ property.address | showProp('city') }} {{ property.address | showProp('province') }}, {{ property.address | showProp('country') }}</div>
                <div class="item">{{ property.address | showProp('postal') }}</div>              
              </div>
              
              <div class="icon-section">
                <i class="icon mdi mdi-24px mdi-account-multiple"></i>

                <div v-if="ticket.reporters.length > 0" class="item" v-for="(reporter, i) in ticket.reporters" :key="i">
                  <div class="avatar">
                    <vo-avatar :user="$store.getters.user(reporter)" :size="24"></vo-avatar>
                  </div>
                  <div class="name" :class="{selectable: hasPermissionsInProperty(['user'], selected.property)}" @click="openUser(reporter)">
                    {{ $store.getters.user(reporter).fullName }}
                  </div>
                </div>

                <div v-if="ticket.reporters.length === 0" class="item">
                  <div class="empty">No users assigned to this unit.</div>
                </div>
              </div>
            
            </div>
          </div>

          <div class="vo-footer" v-if="hasPermissions(['ticketManager', 'ticketAdmin'], selected.property)">
            
            <v-btn v-if="!editing" flat class="vo-button" @click="toggleEditing">
              <v-icon>mdi-pencil</v-icon>
              <span>Edit Description</span>
            </v-btn>
            <div v-else class="right">
              <v-btn :disabled="descLockout" flat class="vo-button" @click="cancelEditDesc">
                <span>Cancel</span>
              </v-btn>
              <v-btn :loading="descLockout" flat class="vo-button green" @click="saveEditDesc">
                <span>Save</span>
              </v-btn>
            </div>
            <div class="clear"></div>
          
          </div>
        </div> <!-- Details Card -->

        <vo-ticket-manage-tools v-if="hasPermissions(['ticketManager', 'ticketAdmin'], selected.property)" ref="ticketManageTools" :page="page" :selected="selected"></vo-ticket-manage-tools>

        <vo-ticket-note v-if="hasPermissions(['ticketManager', 'ticketAdmin'], selected.property)" ref="ticketNote" :page="page" :selected="selected"></vo-ticket-note>

      </template>

      <template v-else>
        <vo-empty-state text="SELECT A TICKET"></vo-empty-state>
      </template>
    
    </div>

    <vo-details-header :page="page" :rightButton="{icon: 'mdi-forum', query: 'chat'}"></vo-details-header>
  
  </div>
</template>

<script>

  import DetailsMixin from '../mixins/detailsMixin.js';
  import permissionMixin from '../mixins/permissionMixin.js';

  export default {
    mixins: [DetailsMixin, permissionMixin],
    data () {
      return {
        ticket: {

        },
        statuses: [],
        ticketTags: [],
        assignees: [],
       
        editing: false,
        rules: {
          description: [
            function (e) {
              return e.length === 0 || !window.regex.description.test(e) ? 'Please enter a valid description.' : true
            }
          ]
        },

        descLockout: false,
      }
    },

    methods: {

      loadTicket () {
        this.selectFromDetails(this.page, 'loadTicket', {
          ticket: this.$route.query.details
        });
      },

      openUnit () {
        if (!this.hasPermissionsInProperty(['unit'], this.selected.property)) return;
      
        this.$router.push({
          name: 'units',
          query: {
            details: this.selected.unit
          }
        });
      },

      openProperty () {
        if (!this.hasPermissions(['property'])) return;

      this.$router.push({
          name: 'properties',
          query: {
            details: this.selected.property
          }
        });
      },

      openUser (item) {
        if (!this.hasPermissionsInProperty(['user'], this.selected.property)) return;
        
        this.$router.push({
          name: 'joined',
          query: {
            details: item
          }
        });
      },

      selectedChanged () {

        // Check Selected is Set
        if (!this.selected) return;

        // Check that the ticket is changed
        if (!this.ticket._id || this.ticket._id !== this.selected._id) {

          this.$nextTick(() => {

            // Scroll to top
            this.$refs.scrollArea.scrollTop = 0;            

            // Updates Ticket Manager Tools
            if (this.$refs.ticketManageTools) this.$refs.ticketManageTools.selectedChanged();

          });
        }

        this.ticket = {
          ...this.selected
        }; // Copy, not reference
      
      },

      openGallery (index) {
        Gallery.show(this.selected.images, index);
      },

      toggleEditing () {
        this.editing = true;
        this.$nextTick(() => {
          this.$refs.inputDescription.focus();
        });
      },

      cancelEditDesc () {
        this.$refs.inputDescription.blur();
        this.ticket.body = this.selected.body;
        this.editing = false;
      },

      saveEditDesc () {
        if (this.descLockout) return;
        this.descLockout = true;   

        this.$refs.inputDescription.validate(true);
        if (!this.$refs.inputDescription.valid) {
          Toast.show('Please correct the highlighted errors.', 'error');
          this.descLockout = false;   
          return;
        }
    
        this.$store.dispatch('updateTicket', {
          ticket: {
            _id: this.selected._id,
            body: this.ticket.body
          },
          property: this.selected.property
        })
        .then(data => {
          Toast.show('Ticket updated.', 'success');
          this.editing = false;
          this.descLockout = false;
        })
        .catch(e => {
          console.log(e);
          Toast.showError(e);
          this.descLockout = false;
        });
      }

    },
    
    computed: {

      selected () {
        return this.$store.getters.ticket(this.page.selected);
      },

      property () {
        return this.$store.getters.property(this.selected.property);
      },

      unit () {
        return this.$store.getters.unit(this.selected.unit);
      }

    },
    
    watch: {

      'selected' () {
        this.selectedChanged();
      },

      '$route.query.details' () {
        this.loadTicket();
      }

    },
    
    created () {
      // IMPROVE: Other pages use Selected as an ID, and the name of the model as the full object referencing selected. This page should be updated to reflect that
      
      if (this.page.selected && this.$route.query.details && this.page.selected !== this.$route.query.selected) {
        console.log('this should change selected!');
        Vue.set(this.page, 'selected', this.$route.query.details);
      }

      if (this.page.selected) this.selectedChanged(); // We need to force this to happen when the page is created, since we depend on the change to load some data
      else this.loadTicket();
    }
  }

</script>

<style lang="scss" scoped>

  @import '../css/global.scss';

  #ticketDetails {

    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;

    @include fontPrimary400();
    
    .section-content {
      @include scrollContainer();
      top: 48px;

      background: $lightGreen; 
      padding: 16px;
      box-sizing: border-box;
    }

    .vo-card {

      .header {
        position: relative;
        height: 81px;
        width: 100%;
        border-bottom: 1px solid $black12;

        .status {
          position: absolute;
          top: 0;
          left: 0;
          bottom: 0;
          width: 6px;
        }

        .left {
          position: absolute;
          top: 14px;
          left: 22px;
          bottom: 16px;
          right: 150px;

          .unit {
            @include textEllipsis();
            font-size: 18px;
            color: $black87;
          }

          .property {
            @include textEllipsis();

            font-size: 16px;
            color: $black54;
            margin: 3px 0 0;
          }
        }

        .right {
          position: absolute;
          top: 20px;
          bottom: 16px;
          right: 16px;
          text-align: right;

          .date {
            font-size: 16px;
            color: $black54;
          }
           
          .status-text {
            color: $black54;
            text-transform: uppercase;
            font-size: 12px;
            letter-spacing: .5px;
            margin: 6px 0 0;
          }

        }

      } // header
    } //details card

    // Images
    .image-preview {
      
      .image-thumb {
        position: relative;
        width: 115px;
        height: 115px;
        background-size: contain;
        background-repeat: no-repeat;
        background-position: center center;
        float: left;
        cursor: pointer;
        transform: scale3d(1, 1, 1);
        transition: all .3s ease;
        border: $borderBlack12;
        margin: 8px 8px 0 0;

        &:hover {
          transform: scale3d(1.05, 1.05, 1);          
        }
      }

    }

    .images-empty {
      @include title();
      @include fontPrimary500;
      text-align: center;
      padding: 16px;
      color: $black24;
    }

    .header-small {
      padding: 16px;
      box-sizing: border-box;
      border-bottom: $borderBlack12;
      @include fontPrimary400;
      @include title();
      font-size: 18px;
    }  

    .details {
      padding: 16px;
      box-sizing: border-box;

      &.notes {
        border-bottom: $borderBlack12;
      }

      .section {
        margin: 0 0 24px;

        .section-title {
          @include fontPrimary500();

          position: relative;
          text-transform: uppercase;
          color: $black54;
          font-size: 14px;
          letter-spacing: .5px;
          padding: 16px 0 5px;
          border-bottom: 1px solid $black12;
          
          .number {
            float: right;
          }

          .status-radio {
            text-transform: capitalize;
          }

          .button {
            position: absolute;
            top: 0;
            right: -10px;
          }
        }
  
        .title-value {
          margin: 16px 0;
        }
  
        .description {
          white-space: pre-line;
          font-size: 14px;
        }

        .small-fields {

          text-align: right;

          .small-input {
            width: 140px;
            display: inline-block;
            margin: 0 0 0 16px;
          }

        }

        .save-note {
          float: right;
        }

        .icon-section {

          position: relative;
          padding: 16px 16px 24px;

          .icon {
            position: absolute;
            left: 8px;
            top: 18px;
          }

          .item {
            padding: 0 0 4px 24px;
            font-size: 16px;
            color: $black87;

            .empty {
              opacity: .24;
              pointer-events: none;
              margin: 3px;
            }

            &.selectable {
              color: $primary;
              cursor: pointer;
            }
    
            .avatar {
              display: inline-block;
            }

            .name {
              display: inline-block;
              margin: 0 0 0 6px;
              line-height: 30px;

              &.selectable {
                color: $primary;
                cursor: pointer;
              }
      
            }
          }

        } // icon-section
     
      } // section
    }// details

  }

</style>



// WEBPACK FOOTER //
// src/components/TicketDetails.vue